import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { roleOf, ROLE_LABELS, can } from "@/lib/roles";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import ThesisProgress from "@/components/ThesisProgress";
import {
  Inbox, FileText, GraduationCap, BarChart3, ArrowRight, Sparkles, Clock,
  CheckCircle2, Upload, RotateCcw, BookOpen, AlarmClock, TriangleAlert,
} from "lucide-react";

const STAT_ICONS = { applications: Inbox, concept_papers: FileText, active_supervisions: GraduationCap, chapters: BarChart3 };
const STAT_LABELS = { applications: "Applications", concept_papers: "Concept Papers", active_supervisions: "Active Supervisions", chapters: "Chapter Submissions" };

const CP_STATUS = {
  analyzing: "AI Analyzing", pending_review: "Pending Review",
  approved: "Approved", revision_requested: "Revise & Resubmit", declined: "Declined",
};

export default function Dashboard() {
  const { user } = useAuth();
  const role = roleOf(user);
  if (role === "student") return <StudentDashboard />;
  return <AdminDashboard />;
}

const HONORIFICS = new Set(["dr", "dr.", "prof", "prof.", "professor", "mr", "mr.", "mrs", "mrs.", "ms", "ms.", "mx", "mx."]);
function firstName(name) {
  const parts = (name || "").trim().split(/\s+/);
  const first = parts.find((p) => !HONORIFICS.has(p.toLowerCase()));
  return first || parts[0] || "";
}

/* ---------------------------------- Student ---------------------------------- */
function StudentDashboard() {
  const { user, tenant } = useAuth();
  const navigate = useNavigate();
  const [ov, setOv] = useState(null);

  useEffect(() => { api.get("/my/overview").then((r) => setOv(r.data)).catch(() => {}); }, []);

  const sup = ov?.supervisions?.[0];
  const prog = sup?.progression;
  const approvedCount = prog?.stages?.filter((s) => s.status === "approved").length || 0;
  const totalStages = prog?.stages?.length || 5;
  const nextAction = prog?.next_submittable
    ? `Submit ${prog.next_submittable}`
    : prog?.blocked_reason || "Awaiting supervisor review";
  const latestCp = ov?.concept_papers?.find((c) => c.status === "approved") || ov?.concept_papers?.[0];

  return (
    <div className="space-y-8 animate-fade-up">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Welcome, {firstName(user?.name)}</h1>
        <p className="text-muted-foreground mt-1">Student · {tenant?.name}</p>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="student-kpis">
        <Card className="p-5">
          <BookOpen className="h-5 w-5 brand-text" />
          <p className="text-lg font-heading font-bold mt-2 truncate">{sup?.stage || "Not started"}</p>
          <p className="text-xs text-muted-foreground">Current stage</p>
        </Card>
        <Card className="p-5">
          <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          <p className="text-3xl font-heading font-bold mt-2">{approvedCount}/{totalStages}</p>
          <p className="text-xs text-muted-foreground">Chapters approved</p>
        </Card>
        <Card className="p-5">
          <FileText className="h-5 w-5 brand-text" />
          <p className="text-lg font-heading font-bold mt-2">{latestCp ? CP_STATUS[latestCp.status] : "None"}</p>
          <p className="text-xs text-muted-foreground">Concept paper</p>
        </Card>
        <Card className="p-5">
          {prog?.next_submittable ? <Upload className="h-5 w-5 brand-text" /> : <Clock className="h-5 w-5 text-amber-600" />}
          <p className="text-sm font-medium mt-2 leading-snug">{prog?.next_submittable ? "Ready to submit" : "Waiting"}</p>
          <p className="text-xs text-muted-foreground">Next action</p>
        </Card>
      </div>

      {/* Next action banner */}
      {sup && (
        <Card className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="next-action-banner">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md bg-secondary flex items-center justify-center brand-text">
              {prog?.next_submittable ? <Upload className="h-5 w-5" /> : <Clock className="h-5 w-5" />}
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Your next step</p>
              <p className="font-heading font-semibold">{nextAction}</p>
              <p className="text-xs text-muted-foreground mt-1">Chapters are submitted one at a time — the next unlocks once the previous is approved.</p>
            </div>
          </div>
          <button data-testid="open-supervision" onClick={() => navigate(`/supervisions/${sup.id}`)}
            className="brand-bg text-white px-4 py-2 rounded-md text-sm flex items-center gap-2 hover:opacity-90 shrink-0">
            Open workspace <ArrowRight className="h-4 w-4" />
          </button>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Progression */}
        <Card className="p-6 lg:col-span-2">
          <div className="flex items-center gap-2 mb-5">
            <GraduationCap className="h-4 w-4 brand-text" />
            <h3 className="font-heading font-semibold">Thesis progression (ARU guidelines)</h3>
          </div>
          {prog ? <ThesisProgress progression={prog} /> : (
            <p className="text-sm text-muted-foreground">You don't have an active supervision yet. Your Research Office will allocate a supervisor once your concept paper is approved.</p>
          )}
        </Card>

        {/* Concept papers */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="h-4 w-4 brand-text" />
            <h3 className="font-heading font-semibold">Concept papers</h3>
          </div>
          <div className="space-y-2" data-testid="student-concepts">
            {(ov?.concept_papers || []).map((c) => (
              <button key={c.id} onClick={() => navigate("/concept-papers")}
                className="w-full text-left rounded-md border border-border p-3 hover:bg-secondary/50 transition-colors">
                <p className="text-sm font-medium truncate">{c.title}</p>
                <Badge variant="outline" className="mt-1 text-[11px]">{CP_STATUS[c.status] || c.status}</Badge>
              </button>
            ))}
            {(!ov?.concept_papers || ov.concept_papers.length === 0) && (
              <button onClick={() => navigate("/concept-papers")} className="text-sm brand-text hover:underline">
                Submit your concept paper →
              </button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ---------------------------------- Admin/Staff/Supervisor/QA ---------------------------------- */
function AdminDashboard() {
  const { user, tenant } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const role = roleOf(user);

  useEffect(() => {
    if (can(user, "analytics")) api.get("/analytics").then((r) => setStats(r.data)).catch(() => {});
  }, [user]);

  const quick = [
    { key: "admissions", to: "/admissions", label: "Admissions", desc: "Review applications & completeness", icon: Inbox },
    { key: "concept", to: "/concept-papers", label: "Concept Papers", desc: "AI diagnostics & review decisions", icon: FileText },
    { key: "supervision", to: "/supervisions", label: "Supervision", desc: "Chapters, feedback & milestones", icon: GraduationCap },
    { key: "analytics", to: "/analytics", label: "Analytics", desc: "Workload, QA & performance", icon: BarChart3 },
  ].filter((q) => can(user, q.key));

  return (
    <div className="space-y-8 animate-fade-up">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Welcome, {firstName(user?.name)}</h1>
        <p className="text-muted-foreground mt-1">{ROLE_LABELS[role]} · {tenant?.name || "Platform Console"}</p>
      </div>

      {role === "super_admin" && (
        <Card className="p-6 flex items-center justify-between">
          <div>
            <h3 className="font-heading font-semibold text-lg">Manage institutions</h3>
            <p className="text-sm text-muted-foreground">Onboard new tenants, configure branding and admins.</p>
          </div>
          <button data-testid="go-institutions" onClick={() => navigate("/institutions")}
            className="brand-bg text-white px-4 py-2 rounded-md text-sm flex items-center gap-2 hover:opacity-90">
            Institutions <ArrowRight className="h-4 w-4" />
          </button>
        </Card>
      )}

      {role === "registrar" && <RegistrarStats navigate={navigate} />}

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-stats">
          {Object.entries(stats.totals)
            .filter(([k]) => k !== "applications" || can(user, "admissions"))
            .map(([k, v]) => {
            const Icon = STAT_ICONS[k] || BarChart3;
            return (
              <Card key={k} className="p-5">
                <div className="flex items-center justify-between">
                  <Icon className="h-5 w-5 brand-text" />
                  <span className="text-3xl font-heading font-bold">{v}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-2">{STAT_LABELS[k]}</p>
              </Card>
            );
          })}
        </div>
      )}

      {stats?.human_control && (
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="h-4 w-4 brand-text" />
            <h3 className="font-heading font-semibold">Human-in-the-loop governance</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Metric icon={CheckCircle2} label="AI advice accepted" value={stats.human_control.accepted} />
            <Metric icon={RotateCcw} label="Modified / overridden" value={stats.human_control.modified_or_overridden} />
            <Metric icon={BarChart3} label="Accept rate" value={`${stats.human_control.accept_rate}%`} />
            <Metric icon={Clock} label="Awaiting human feedback" value={stats.pending_feedback} />
          </div>
        </Card>
      )}

      {["supervisor", "tenant_admin", "staff", "qa", "director"].includes(role) && <ReviewQueue navigate={navigate} />}

      <div className="grid md:grid-cols-2 gap-4">
        {quick.map(({ key, to, label, desc, icon: Icon }) => (
          <button key={key} data-testid={`quick-${key}`} onClick={() => navigate(to)}
            className="group text-left rounded-lg border border-border bg-card p-6 hover:-translate-y-[2px] hover:shadow-sm transition-all">
            <div className="flex items-start justify-between">
              <div className="h-10 w-10 rounded-md flex items-center justify-center brand-text bg-secondary">
                <Icon className="h-5 w-5" />
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
            </div>
            <h3 className="font-heading font-semibold mt-4">{label}</h3>
            <p className="text-sm text-muted-foreground">{desc}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

function RegistrarStats({ navigate }) {
  const [apps, setApps] = useState(null);
  useEffect(() => { api.get("/applications").then((r) => setApps(r.data)).catch(() => setApps([])); }, []);
  if (!apps) return null;
  const awaitingDocs = apps.filter((a) => a.status === "awaiting_documents").length;
  const decided = new Set(["admitted", "rejected"]);
  const awaitingDecision = apps.filter((a) => !a.decided_by && a.status !== "awaiting_documents" && !decided.has(a.status)).length;
  const admitted = apps.filter((a) => a.status === "admitted").length;
  const tiles = [
    { id: "total", label: "Total applications", value: apps.length, icon: Inbox },
    { id: "awaiting-documents", label: "Awaiting documents", value: awaitingDocs, icon: Upload },
    { id: "awaiting-decision", label: "Awaiting decision", value: awaitingDecision, icon: Clock },
    { id: "admitted", label: "Admitted", value: admitted, icon: CheckCircle2 },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="registrar-kpis">
      {tiles.map(({ id, label, value, icon: Icon }) => (
        <button key={id} data-testid={`registrar-kpi-${id}`}
          onClick={() => navigate("/admissions")}
          className="text-left rounded-lg border border-border bg-card p-5 hover:-translate-y-[2px] hover:shadow-sm transition-all">
          <div className="flex items-center justify-between">
            <Icon className="h-5 w-5 brand-text" />
            <span className="text-3xl font-heading font-bold">{value}</span>
          </div>
          <p className="text-sm text-muted-foreground mt-2">{label}</p>
        </button>
      ))}
    </div>
  );
}

function ReviewQueue({ navigate }) {  const [q, setQ] = useState(null);
  useEffect(() => { api.get("/review-queue").then((r) => setQ(r.data)).catch(() => setQ({ items: [], overdue_count: 0, sla_days: 7 })); }, []);
  if (!q) return null;
  const items = q.items || [];

  return (
    <Card className="p-6" data-testid="review-queue">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <AlarmClock className="h-4 w-4 brand-text" />
          <h3 className="font-heading font-semibold">Review turnaround queue</h3>
        </div>
        {q.overdue_count > 0 && (
          <Badge className="bg-red-100 text-red-700 gap-1" data-testid="overdue-count">
            <TriangleAlert className="h-3 w-3" />{q.overdue_count} overdue
          </Badge>
        )}
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Chapters awaiting your decision. Anything waiting {q.sla_days}+ days is flagged overdue.
      </p>

      {items.length === 0 ? (
        <div className="flex items-center gap-2 rounded-md border border-border bg-secondary/40 p-4 text-sm text-muted-foreground" data-testid="review-queue-empty">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Nothing awaiting your review — you're up to date.
        </div>
      ) : (
        <div className="space-y-2" data-testid="review-queue-list">
          {items.map((it) => (
            <button
              key={it.chapter_id}
              data-testid={`queue-item-${it.chapter_id}`}
              onClick={() => navigate(`/supervisions/${it.supervision_id}`)}
              className={`w-full text-left flex items-center justify-between gap-3 rounded-md border p-3 transition-colors hover:bg-secondary/50 ${
                it.overdue ? "border-red-200 bg-red-50/60" : "border-border"
              }`}
            >
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{it.title} <span className="text-muted-foreground font-normal">· v{it.version}</span></p>
                <p className="text-xs text-muted-foreground truncate">{it.student_name}</p>
              </div>
              <div className={`shrink-0 flex items-center gap-1 text-xs font-medium ${it.overdue ? "text-red-700" : "text-amber-700"}`}>
                <Clock className="h-3.5 w-3.5" />
                {it.days_waiting < 1 ? "<1 day" : `${Math.round(it.days_waiting)} day${Math.round(it.days_waiting) === 1 ? "" : "s"}`}
              </div>
            </button>
          ))}
        </div>
      )}
    </Card>
  );
}

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="rounded-md border border-border p-4">
      <Icon className="h-4 w-4 text-muted-foreground" />
      <p className="text-2xl font-heading font-bold mt-2">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}
